import { Button } from '@astryxdesign/core/Button';
import { Grid } from '@astryxdesign/core/Grid';
import { HStack } from '@astryxdesign/core/HStack';
import { Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { useAvailability } from '@/api/public';
import type { Studio, StudioService } from '@/api/schemas';
import { EmptyView, ErrorView, LoadingRows } from '@/components/StateViews';
import { addIsoDays, fmtDuration, fmtInTz, fmtMoney, fmtTime, localDateToInstant } from '@/lib/format';

const WINDOW = 7;

export function TimeStep({
  studio,
  service,
  date,
  onDate,
  onTime,
}: {
  studio: Studio;
  service: StudioService;
  date: string | undefined;
  onDate: (d: string) => void;
  onTime: (iso: string) => void;
}) {
  const tz = studio.timezone;
  const [windowStart, setWindowStart] = useState(() => date ?? studio.today);
  const q = useAvailability(studio.slug, service.id, windowStart, WINDOW);
  const days = q.data?.days ?? [];
  const selected = days.find((d) => d.date === date);

  // Default to the first day that still has a free slot.
  useEffect(() => {
    if (!q.data || (date && days.some((d) => d.date === date))) return;
    const first = days.find((d) => d.slots.some((s) => s.available)) ?? days[0];
    if (first) onDate(first.date);
  }, [q.data, date, days, onDate]);

  const canPrev = windowStart > studio.today;
  const canNext = q.data ? addIsoDays(windowStart, WINDOW) <= q.data.horizonEnd : false;
  const longJob = service.durationMin >= 1440;

  const summary = useMemo(
    () => `${service.name} · ${fmtDuration(service.durationMin)} · ${fmtMoney(service.priceCents, studio.currency, service.priceIsFrom)}`,
    [service, studio.currency],
  );

  return (
    <VStack gap={4}>
      <Text color="secondary">{summary}</Text>
      {longJob && (
        <Text type="supporting">Машина остаётся у нас на {fmtDuration(service.durationMin)}: выбранное время — это время, когда вы её привозите.</Text>
      )}

      <HStack gap={2} vAlign="center">
        <Button
          label="Предыдущие дни"
          isIconOnly
          icon={<CaretLeft size={18} />}
          variant="ghost"
          isDisabled={!canPrev}
          onClick={() => setWindowStart((w) => (addIsoDays(w, -WINDOW) < studio.today ? studio.today : addIsoDays(w, -WINDOW)))}
        />
        <div className="date-strip" role="radiogroup" aria-label="День">
          {q.isPending && !q.data
            ? Array.from({ length: WINDOW }, (_, i) => <span key={i} className="date-chip date-chip--ghost" />)
            : days.map((d) => {
                const free = d.slots.filter((s) => s.available).length;
                const instant = localDateToInstant(d.date, tz);
                const isSel = d.date === date;
                return (
                  <button
                    key={d.date}
                    type="button"
                    role="radio"
                    aria-checked={isSel}
                    className="date-chip"
                    data-selected={isSel}
                    data-closed={!d.isOpen || free === 0}
                    onClick={() => onDate(d.date)}
                    aria-label={`${fmtInTz(instant, tz, 'd MMMM, EEEE')}: ${!d.isOpen ? 'выходной' : free ? `свободно ${free}` : 'всё занято'}`}
                  >
                    <span className="date-chip__dow">{fmtInTz(instant, tz, 'EEEEEE')}</span>
                    <span className="date-chip__day">{fmtInTz(instant, tz, 'd')}</span>
                    <span className="date-chip__dot" aria-hidden data-free={free > 0} />
                  </button>
                );
              })}
        </div>
        <Button
          label="Следующие дни"
          isIconOnly
          icon={<CaretRight size={18} />}
          variant="ghost"
          isDisabled={!canNext}
          onClick={() => setWindowStart((w) => addIsoDays(w, WINDOW))}
        />
      </HStack>

      {q.isError && !q.data ? (
        <ErrorView compact error={q.error} onRetry={() => void q.refetch()} />
      ) : !selected ? (
        <LoadingRows rows={2} label="Загружаем свободное время" />
      ) : !selected.isOpen ? (
        <EmptyView title="В этот день студия не работает" description="Выберите другой день." />
      ) : selected.slots.length === 0 ? (
        <EmptyView title="На сегодня запись закрыта" description="Ближайшее время — в следующие дни." />
      ) : (
        <VStack gap={2}>
          <Text type="label">{fmtInTz(localDateToInstant(selected.date, tz), tz, 'd MMMM, EEEE')}</Text>
          <Grid columns={{ minWidth: 76 }} gap={2} role="list" aria-label="Время">
            {selected.slots.map((s) => {
              const t = fmtTime(s.startsAt, tz);
              return s.available ? (
                <Button key={s.startsAt} label={t} variant="secondary" onClick={() => onTime(s.startsAt)} />
              ) : (
                <Button key={s.startsAt} label={`${t}, занято`} isDisabled>
                  <Text hasStrikethrough color="disabled">
                    {t}
                  </Text>
                </Button>
              );
            })}
          </Grid>
          <Text type="supporting">Зачёркнутое время занято. Время указано по часовому поясу студии.</Text>
        </VStack>
      )}
      {q.isFetching && q.data && <Text type="supporting">Обновляем…</Text>}
    </VStack>
  );
}
